import React from 'react';
import { useSelector } from 'react-redux';
import MetaTags from 'react-meta-tags';
import Fade from 'react-reveal/Fade';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Loader } from '../components/Loader';
import { KestrelTrace } from '../components/KestrelTrace';
import kestrelLogo from '../assets/images/kestrel/kestrel-logo.svg';
import gunzLogo from '../assets/images/kestrel/gunz-logo.png';
import kestrelLocalization from '../kestrelLocalization';

const CONTACT_EMAIL = 'kestrel@gunzillagames.com';
const JITTER_ENTROPY = '0.82';

// Visual data for the "three attempts" panel; copy lives in kestrelLocalization.
const ATTEMPT_MATCHES = [
    { match: '100%', verdict: 'banned' },
    { match: '97%', verdict: 'matched' },
    { match: '96%', verdict: 'matched' },
];

const scrollToSection = (event) => {
    const target = document.getElementById(event.currentTarget.hash.slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const Eyebrow = ({ num, children }) => (
    <div className='kestrel-page__eyebrow'>
        <span className='kestrel-page__accent'>{num}</span> {children}
    </div>
);

const Hero = ({ t }) => (
    <section className='kestrel-hero'>
        <div className='kestrel-hero__dots' />
        <div className='kestrel-page__container kestrel-hero__inner'>
            <div className='kestrel-page__eyebrow kestrel-hero__tags'>
                <span className='kestrel-page__accent'>{t.HERO_TAG_PRODUCT}</span>
                <span>{t.HERO_TAG_CATEGORY}</span>
                <span className='kestrel-page__accent kestrel-hero__status'>
                    <span className='kestrel-hero__live-dot' />
                    {t.HERO_TAG_STATUS}
                </span>
            </div>
            <div className='kestrel-hero__grid'>
                <div className='kestrel-hero__brand'>
                    <img className='kestrel-hero__logo' src={kestrelLogo} alt='Kestrel anti-cheat' />
                    <div className='kestrel-page__eyebrow kestrel-hero__caption'>
                        <span className='kestrel-hero__caption-line' />
                        {t.HERO_CAPTION}
                    </div>
                </div>
                <div className='kestrel-hero__intro'>
                    <h1 className='kestrel-hero__title'>
                        {t.HERO_TITLE_BEFORE}
                        <span className='kestrel-page__accent'>{t.HERO_TITLE_ACCENT}</span>
                        {t.HERO_TITLE_AFTER}
                    </h1>
                    <p className='kestrel-page__text'>{t.HERO_TEXT}</p>
                    <div className='kestrel-hero__actions'>
                        <a href='#how' onClick={scrollToSection} className='kestrel-button kestrel-button--solid'>
                            {t.HERO_CTA_HOW} <span>&gt;</span>
                        </a>
                        <a href='#validators' onClick={scrollToSection} className='kestrel-button kestrel-button--outline'>
                            {t.HERO_CTA_VALIDATORS} <span>&gt;</span>
                        </a>
                    </div>
                </div>
            </div>
            <div className='kestrel-trace-panel'>
                <div className='kestrel-trace-panel__head'>
                    <span>{t.TRACE_TITLE}</span>
                    <span className='kestrel-trace-panel__legend'>
                        <span className='kestrel-page__accent'>— {t.TRACE_LEGEND_HUMAN}</span>
                        <span>— {t.TRACE_LEGEND_SYNTHETIC}</span>
                    </span>
                </div>
                <div className='kestrel-trace-panel__chart'>
                    <KestrelTrace />
                </div>
                <div className='kestrel-trace-panel__stats'>
                    <div className='kestrel-trace-panel__stat'>
                        <div className='kestrel-trace-panel__stat-label'>{t.TRACE_STAT_CORRECTION}</div>
                        <div className='kestrel-trace-panel__stat-value'>{t.TRACE_STAT_CORRECTION_VALUE}</div>
                    </div>
                    <div className='kestrel-trace-panel__stat'>
                        <div className='kestrel-trace-panel__stat-label'>{t.TRACE_STAT_ENTROPY}</div>
                        <div className='kestrel-trace-panel__stat-value'>{JITTER_ENTROPY}</div>
                    </div>
                    <div className='kestrel-trace-panel__stat'>
                        <div className='kestrel-trace-panel__stat-label'>{t.TRACE_STAT_VERDICT}</div>
                        <div className='kestrel-trace-panel__stat-value kestrel-page__accent'>{t.TRACE_STAT_VERDICT_VALUE}</div>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const Shift = ({ t }) => (
    <section className='kestrel-section'>
        <div className='kestrel-page__container kestrel-shift'>
            <div className='kestrel-section__heading'>
                <Eyebrow num='01'>{t.SHIFT_LABEL}</Eyebrow>
                <h2 className='kestrel-section__title'>
                    {t.SHIFT_TITLE}
                    <br />
                    <span className='kestrel-page__accent'>{t.SHIFT_TITLE_ACCENT}</span>
                </h2>
            </div>
            <div className='kestrel-shift__compare'>
                <div className='kestrel-shift__row'>
                    <div className='kestrel-page__eyebrow'>{t.SHIFT_LEGACY_LABEL}</div>
                    <p className='kestrel-page__text'>{t.SHIFT_LEGACY_TEXT}</p>
                </div>
                <div className='kestrel-shift__row kestrel-shift__row--kestrel'>
                    <div className='kestrel-page__eyebrow kestrel-page__accent'>{t.SHIFT_KESTREL_LABEL}</div>
                    <p className='kestrel-page__text kestrel-page__text--bright'>{t.SHIFT_KESTREL_TEXT}</p>
                </div>
            </div>
        </div>
    </section>
);

const HowItWorks = ({ t }) => (
    <section id='how' className='kestrel-section'>
        <div className='kestrel-page__container kestrel-how'>
            <div className='kestrel-section__heading kestrel-how__heading'>
                <Eyebrow num='02'>{t.HOW_LABEL}</Eyebrow>
                <h2 className='kestrel-section__title'>{t.HOW_TITLE}</h2>
            </div>
            <div className='kestrel-how__pillars'>
                {t.PILLARS.map((pillar, index) => (
                    <div className='kestrel-how__pillar' key={pillar.title}>
                        <div className='kestrel-page__eyebrow'>{String(index + 1).padStart(2, '0')}</div>
                        <h3 className='kestrel-how__pillar-title'>{pillar.title}</h3>
                        <p className='kestrel-page__text kestrel-page__text--small'>{pillar.body}</p>
                        <div className='kestrel-page__eyebrow kestrel-page__accent kestrel-how__pillar-note'>&gt; {pillar.note}</div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const Bans = ({ t }) => (
    <section className='kestrel-section kestrel-section--raised'>
        <div className='kestrel-page__container kestrel-bans'>
            <div className='kestrel-section__heading'>
                <Eyebrow num='03'>{t.BANS_LABEL}</Eyebrow>
                <h2 className='kestrel-section__title'>
                    {t.BANS_TITLE_1}
                    <br />
                    {t.BANS_TITLE_2}
                </h2>
                <p className='kestrel-page__text'>{t.BANS_TEXT}</p>
                <ul className='kestrel-bans__points'>
                    {t.BANS_POINTS.map(point => (
                        <li key={point}><span className='kestrel-page__accent'>&gt;</span>{point}</li>
                    ))}
                </ul>
            </div>
            <div className='kestrel-bans__panel'>
                <div className='kestrel-page__eyebrow kestrel-bans__panel-head'>{t.BANS_PANEL_TITLE}</div>
                {t.BANS_ATTEMPTS.map((attempt, index) => {
                    const { match, verdict } = ATTEMPT_MATCHES[index];
                    return (
                        <div className={`kestrel-bans__attempt kestrel-bans__attempt--${verdict}`} key={attempt.label}>
                            <div className='kestrel-bans__attempt-info'>
                                <div className='kestrel-page__eyebrow'>{attempt.label}</div>
                                <div className='kestrel-bans__attempt-device'>{attempt.device}</div>
                            </div>
                            <div className='kestrel-bans__attempt-match'>
                                <div className='kestrel-page__eyebrow'>{t.BANS_MATCH}</div>
                                <div className='kestrel-bans__meter'>
                                    <div className='kestrel-bans__meter-fill' style={{ width: match }} />
                                </div>
                            </div>
                            <div className='kestrel-bans__verdict'>
                                <span className='kestrel-bans__verdict-dot' />
                                {verdict === 'banned' ? t.VERDICT_BANNED : t.VERDICT_MATCHED}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    </section>
);

const Validators = ({ t }) => (
    <section id='validators' className='kestrel-section kestrel-section--last'>
        <div className='kestrel-page__container kestrel-validators'>
            <div className='kestrel-section__heading'>
                <Eyebrow num='04'>{t.VALIDATORS_LABEL}</Eyebrow>
                <img className='kestrel-validators__logo' src={gunzLogo} alt='GUNZ' />
                <h2 className='kestrel-section__title kestrel-section__title--large'>
                    {t.VALIDATORS_TITLE}
                    <br />
                    <span className='kestrel-page__accent'>{t.VALIDATORS_TITLE_ACCENT}</span>
                </h2>
                <p className='kestrel-page__text kestrel-validators__text'>{t.VALIDATORS_TEXT}</p>
            </div>
            <div className='kestrel-validators__card'>
                <div className='kestrel-page__eyebrow kestrel-validators__card-head'>
                    <span className='kestrel-page__accent'>{t.VALIDATORS_CARD_TITLE}</span>
                    <span>{t.VALIDATORS_CARD_BADGE}</span>
                </div>
                <p className='kestrel-page__text kestrel-page__text--small'>{t.VALIDATORS_CARD_TEXT}</p>
                <a href={`mailto:${CONTACT_EMAIL}`} className='kestrel-button kestrel-button--outline kestrel-button--wide'>
                    <span>{t.VALIDATORS_CARD_CTA}</span><span>&gt;</span>
                </a>
            </div>
        </div>
    </section>
);

export const KestrelPage = ({ lang }) => {
    const fontsLoadingCheck = useSelector(state => state.fontsLoadingCheck);
    const t = kestrelLocalization[lang] || kestrelLocalization.en;

    return (
        <>
            <Loader loaded={fontsLoadingCheck} />
            <MetaTags>
                <title>{t.META_TITLE}</title>
                <meta id='meta-title' name='title' content={t.META_TITLE} />
                <meta id='meta-description' name='description' content={t.META_DESCRIPTION} />
                <meta id='og-title' property='og:title' content={t.META_TITLE} />
                <meta id='og-description' property='og:description' content={t.META_DESCRIPTION} />
            </MetaTags>
            <div className={`kestrel-page kestrel-page--${lang}`} lang={lang === 'ua' ? 'uk' : lang}>
                <Header />
                <main className='kestrel-page__main'>
                    <Hero t={t} />
                    <Shift t={t} />
                    <HowItWorks t={t} />
                    <Bans t={t} />
                    <Validators t={t} />
                </main>
                <Fade>
                    <Footer />
                </Fade>
            </div>
        </>
    );
};
